import { HouseholdStudent, RosterProgressStatuses } from '@k12/models';

/** The `enrollmentTypes` placeholder option shown before a school moves a student off its roster. */
const NOT_YET_CERTIFIED_ENROLLMENT_STATUS_ID = 0;
const NOT_ATTENDING_ATTENDANCE_TYPE_ID = 3;

/**
 * Enrollment Status renders the student's attendance type, falling back to a "Not Yet Certified"
 * placeholder while the school still has them on the New or Canceled roster.
 *
 * The roster progress status — not `certificationDate` — decides that placeholder: a school may skip
 * certification and endorse a student outright, leaving them enrolled with no certification date. The
 * date is only used when the payload omits the status. "Not Attending" always shows as-is, since that
 * is a deliberate choice rather than a step the school has yet to take.
 */
export function resolveEnrollmentStatusId(student: HouseholdStudent): number | undefined {
  if (Number(student.attendanceTypeId) === NOT_ATTENDING_ATTENDANCE_TYPE_ID) {
    return student.attendanceTypeId;
  }

  return isAwaitingCertification(student) ? NOT_YET_CERTIFIED_ENROLLMENT_STATUS_ID : student.attendanceTypeId;
}

/**
 * True while the school has yet to move the student off its New (or Canceled) roster.
 *
 * This is the line the enrollment process draws: before it the household may still change the school
 * outright on the edit form, and after it the change has to go through withdrawal/transfer. A student
 * endorsed without being certified is past that line even though they carry no certification date, so
 * the date is only consulted for payloads that omit `rosterProgressStatusId`.
 */
export function isAwaitingCertification(student: HouseholdStudent): boolean {
  const status = student.rosterProgressStatusId;

  if (status == null) {
    return !student.certificationDate;
  }

  return status === RosterProgressStatuses.New || status === RosterProgressStatuses.Canceled;
}
